import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Package, Boxes, ShoppingCart, CreditCard, Bitcoin, Users,
  Truck as TruckIcon, FileCheck, Newspaper, BookOpen, HelpCircle, Tag, Truck,
  BarChart3, Settings, Wallet, Share2, ShieldCheck, ExternalLink,
} from 'lucide-react';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/inventory', label: 'Inventory', icon: Boxes },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard },
  { to: '/admin/crypto', label: 'Crypto Payments', icon: Bitcoin },
  { to: '/admin/customers', label: 'Customers', icon: Users },
  { to: '/admin/suppliers', label: 'Suppliers', icon: TruckIcon },
  { to: '/admin/coas', label: 'COAs', icon: FileCheck },
  { to: '/admin/journal', label: 'Journal', icon: Newspaper },
  { to: '/admin/docs', label: 'Documentation', icon: BookOpen },
  { to: '/admin/faqs', label: 'FAQ', icon: HelpCircle },
  { to: '/admin/discounts', label: 'Discounts', icon: Tag },
  { to: '/admin/shipping', label: 'Shipping', icon: Truck },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings },
  { to: '/admin/payment-settings', label: 'Payment Settings', icon: Wallet },
  { to: '/admin/social', label: 'Social Links', icon: Share2 },
  { to: '/admin/admins', label: 'Administrators', icon: ShieldCheck },
];

export default function AdminLayout() {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen bg-[#0B0F12] flex items-center justify-center"><p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#8A8F95]">Loading…</p></div>;
  if (!user) { navigate('/login'); return null; }
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-forbidden">
        <p className="font-display font-bold uppercase text-2xl">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F0] flex" data-testid="admin-layout">
      <aside className="w-60 bg-[#0B0F12] text-[#C9CDD2] min-h-screen fixed left-0 top-0 bottom-0 overflow-y-auto hidden lg:block">
        <div className="p-5 border-b border-[#1E242B]">
          <p className="font-display font-black text-xl text-white">VQ HEALTH</p>
          <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#6B7178] mt-0.5">Admin Console</p>
        </div>
        <nav className="p-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-[13px] font-mono2 uppercase tracking-wider transition-colors ${isActive ? 'bg-[#1A2129] text-white border-l-2 border-[#E54D2E]' : 'hover:bg-[#151A1F] border-l-2 border-transparent'}`}>
              <n.icon className="w-4 h-4 shrink-0" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1E242B]">
          <Link to="/" data-testid="admin-view-store" className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest text-[#6B7178] hover:text-white">
            <ExternalLink className="w-3.5 h-3.5" /> View storefront
          </Link>
        </div>
      </aside>

      <div className="flex-1 lg:ml-60 min-w-0">
        <div className="lg:hidden bg-[#0B0F12] text-white p-3 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `px-3 py-2 font-mono2 text-[10px] uppercase tracking-wider whitespace-nowrap ${isActive ? 'bg-[#1A2129] text-white' : 'text-[#8A8F95]'}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
        <main className="p-4 sm:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
